import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import ErrorOutlineIcon from 'components/material-symbol/icons/ErrorOutline'
import LockIcon from 'components/material-symbol/icons/Lock'
import useChromeStyles from 'pages/schedule/SelectLessonTime/redesign/styles'
import useStyles from './styles'

// The step 3 card: student row, heading, payment (or other) content, line
// items, optional blocks and the primary action.
// On desktop the button closes the card; on small screens it moves to the
// sticky bottom bar that steps 1 and 2 use (still inside the <form>, so Enter
// and the button submit the same way).
const CheckoutCard = ({
  studentRow,
  heading,
  headingNote,
  children,
  lineItems,
  due,
  dueNote,
  optIn,
  callout,
  error,
  button,
  caption,
  barLabel,
  isCompact,
  onSubmit,
  formProps,
}) => {
  const chrome = useChromeStyles()
  const classes = useStyles()

  const action = (
    <Button
      type="submit"
      variant="contained"
      color="primary"
      className={
        isCompact && barLabel
          ? clsx(chrome.continueButton, classes.noTransform)
          : clsx(classes.payButton, classes.noTransform)
      }
      disabled={button.disabled}
      aria-busy={button.busy || undefined}
      data-testid="complete-booking-button"
    >
      {button.label}
    </Button>
  )

  return (
    <form onSubmit={onSubmit} noValidate {...formProps}>
      <section className={classes.payCard} aria-labelledby="review-heading">
        {studentRow}
        <Typography
          variant="h5"
          component="h1"
          id="review-heading"
          className={classes.heading}
        >
          {heading}
        </Typography>
        {headingNote && <p className={classes.headingNote}>{headingNote}</p>}
        {children}
        {lineItems?.length > 0 && (
          <>
            <hr className={classes.divider} />
            {lineItems.map(item => (
              <p className={classes.lineItem} key={item.label}>
                <span>{item.label}</span>
                <span>{item.amount}</span>
              </p>
            ))}
            {due && (
              <p className={classes.dueRow} data-testid="due-today">
                <span>{due.label}</span>
                <span>{due.amount}</span>
              </p>
            )}
            {dueNote && <p className={classes.lineNote}>{dueNote}</p>}
          </>
        )}
        {optIn && <div className={classes.optIn}>{optIn}</div>}
        {callout && <div className={classes.callout}>{callout}</div>}
        {error && (
          <div className={classes.inlineError} role="alert">
            <ErrorOutlineIcon aria-hidden="true" />
            <span>{error}</span>
          </div>
        )}
        {!isCompact && <div className={classes.actions}>{action}</div>}
        {caption && (
          <p className={classes.caption}>
            <LockIcon aria-hidden="true" />
            <span>{caption}</span>
          </p>
        )}
      </section>
      {isCompact && (
        <div className={chrome.bottomBar} data-testid="sticky-action-bar">
          {barLabel && <p className={chrome.bottomText}>{barLabel}</p>}
          {action}
        </div>
      )}
    </form>
  )
}

CheckoutCard.propTypes = {
  studentRow: PropTypes.node,
  heading: PropTypes.string.isRequired,
  headingNote: PropTypes.string,
  children: PropTypes.node,
  lineItems: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      amount: PropTypes.string.isRequired,
    }),
  ),
  due: PropTypes.shape({
    label: PropTypes.string.isRequired,
    amount: PropTypes.string.isRequired,
  }),
  dueNote: PropTypes.string,
  optIn: PropTypes.node,
  callout: PropTypes.node,
  error: PropTypes.string,
  button: PropTypes.shape({
    label: PropTypes.string.isRequired,
    disabled: PropTypes.bool,
    busy: PropTypes.bool,
  }).isRequired,
  caption: PropTypes.string,
  barLabel: PropTypes.string,
  isCompact: PropTypes.bool,
  onSubmit: PropTypes.func.isRequired,
  formProps: PropTypes.object,
}

export default CheckoutCard
